import { ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { Drawer, PictureInPicture, TopNavLink } from './uiStoreTypes'
import { IRogue } from '@/js/services/RogueTypes'

export const useUIStore = defineStore('ui', () => {
  const overlay = ref<string | null>(null)
  const drawer = ref<Drawer>({})
  const pictureInPicture = ref<PictureInPicture | null>(null)
  const appLastContentReloadAt = ref<number>()
  const appIsNotActive = ref<boolean>(false)
  const hideMbnBanner = ref<boolean>(false)
  // Deliberately not persisted: a dismissed 5-for-5 drawer comes back on refresh.
  const hideFiveForFiveDrawer = ref<boolean>(false)
  /**
   * Hide the site header for a page that owns the whole viewport.
   *
   * Separate from `hideMbnBanner`, which already hides the footer and the MBN
   * banner: that flag is also set by the order-manager quote page, so folding
   * the header into it would have hidden the header there too. This one is only
   * set by the Gym Builder, so no existing page changes.
   */
  const hideSiteChrome = ref<boolean>(false)
  const topNavLinks = ref<TopNavLink[]>([])
  const eventNavLinks = ref<TopNavLink[]>([])
  const eventBaseUrl = ref<string | null | undefined>()
  const eventLogo = ref()

  function showOverlay(newOverlay: string, rogue: IRogue) {
    if (newOverlay) {
      rogue.track.track({ type: 'overlay', overlay: newOverlay })
    }
    overlay.value = newOverlay
  }

  function hideOverlay() {
    overlay.value = null
  }

  function drawerActive(active: Drawer) {
    drawer.value = active
  }

  function showPictureInPicture(video?: PictureInPicture) {
    pictureInPicture.value = video || null
  }

  return {
    overlay,
    drawer,
    pictureInPicture,
    appLastContentReloadAt,
    appIsNotActive,
    hideMbnBanner,
    hideFiveForFiveDrawer,
    hideSiteChrome,
    topNavLinks,
    eventNavLinks,
    eventBaseUrl,
    eventLogo,
    showOverlay,
    hideOverlay,
    drawerActive,
    showPictureInPicture,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUIStore, import.meta.hot))
}
